import { SettingFormDataTypes } from 'src/components/SettingsTab/SettingsTab';

import { Api } from 'src/models/Api.model';
import { Product } from 'src/models/Product.model';
import {
  PracticeSettings,
  UserPermission,
  UserSettings,
} from 'src/models/Settings.model';

import {
  CustomField,
  CustomPracticeProfileFieldEnum,
  CustomUserProfileFieldEnum,
  UserPermissionEnum,
} from '../CustomField.model';
import { CustomFieldData } from '../CustomFieldData.model';
import { PracticeAssociation } from '../PracticeAssociation.model';
import { GalenDataUser } from '../User.model';

const getPracticeDirtyFields = (
  dirtyFields: Partial<
    Readonly<{
      hasMfa?: boolean | undefined;
    }>
  >,
) => {
  return Object.keys(dirtyFields) as Array<keyof SettingFormDataTypes>;
};

const convertPracticeSettings = (
  practiceSettings: CustomFieldData<CustomPracticeProfileFieldEnum>[],
) => {
  const fieldHandlers: Record<
    CustomPracticeProfileFieldEnum,
    (fieldData: string | null, result: PracticeSettings) => void
  > = {
    [CustomPracticeProfileFieldEnum.Broadcast]: (fieldData, result) => {
      result.broadcast = fieldData;
    },
    [CustomPracticeProfileFieldEnum.Product]: (fieldData, result) => {
      result.product = fieldData as Product;
    },
    [CustomPracticeProfileFieldEnum.HasMfa]: (fieldData, result) => {
      result.hasMfa = fieldData === 'true';
    },
  };

  return practiceSettings.reduce((result, { field: { name }, fieldData }) => {
    const handler = fieldHandlers[name];
    if (handler) handler(fieldData, result);
    return result;
  }, {} as PracticeSettings);
};

const convertUserPermissionToObj = (
  userPermissions: UserPermissionEnum[],
): UserPermission => {
  return userPermissions.reduce<UserPermission>(
    (permissions, key) => {
      if (Object.values(UserPermissionEnum).includes(key)) {
        return {
          ...permissions,
          [key]: true,
        };
      }
      return permissions;
    },
    {
      create: false,
      list: false,
      read: false,
      print: false,
      update: false,
    },
  );
};

const convertUserSettings = (
  userSettings: CustomFieldData<CustomUserProfileFieldEnum>[],
): UserSettings => {
  const fieldHandlers: Record<
    CustomUserProfileFieldEnum,
    (fieldData: string | null, result: UserSettings) => void
  > = {
    [CustomUserProfileFieldEnum.UserPermission]: (fieldData, result) => {
      result.userPermission = convertUserPermissionToObj(
        fieldData as unknown as UserPermissionEnum[],
      );
    },
  };

  return userSettings.reduce((result, { field: { name }, fieldData }) => {
    const handler = fieldHandlers[name];
    if (handler) handler(fieldData, result);
    return result;
  }, {} as UserSettings);
};

const updateUserSettings = async ({
  userSettingsData,
  data,
  user,
  userCustomFields,
}: {
  userSettingsData: CustomFieldData<CustomUserProfileFieldEnum>[];
  data: Partial<{
    [key in CustomUserProfileFieldEnum]: string | string[] | null;
  }>;
  user: GalenDataUser;
  userCustomFields?: CustomField<CustomUserProfileFieldEnum>[];
}) => {
  const updatedUserSettings = (
    Object.keys(data) as Array<keyof typeof data>
  ).map((customField) => {
    return {
      field:
        userCustomFields?.find((fieldDef) => fieldDef.name === customField) ||
        userSettingsData.find((setting) => setting.field.name === customField)
          ?.field,
      fieldData: data[customField],
    };
  });

  await Api.remote._update_user_custom_fields(updatedUserSettings, user);
};

const updatePracticeSettings = async (
  practiceDirtyFields: Array<keyof SettingFormDataTypes>,
  practiceSettingsData:
    | CustomFieldData<CustomPracticeProfileFieldEnum>[]
    | undefined,
  data: SettingFormDataTypes,
  practice: PracticeAssociation,
  practiceCustomFields?: CustomField<CustomPracticeProfileFieldEnum>[],
) => {
  const updatedPracticeSettings = practiceDirtyFields.map((field) => {
    return {
      field:
        practiceCustomFields?.find((fieldDef) => fieldDef.name === field) ||
        practiceSettingsData?.find((setting) => setting.field.name === field)
          ?.field,
      fieldData: `${data[field]}`,
    };
  });

  await Api.remote._update_practice_custom_fields(
    updatedPracticeSettings,
    practice,
  );
};

export {
  convertPracticeSettings,
  convertUserPermissionToObj,
  convertUserSettings,
  getPracticeDirtyFields,
  updatePracticeSettings,
  updateUserSettings,
};
